// src/components/admin/GestionLesiones.js
import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import {
    getLesiones,
    crearLesion,
    actualizarLesion,
    eliminarLesion
} from '../../services/art/lesionService';
import ModalConfirmacionCambioEstado from '../ModalConfirmacionCambioEstado';
import MenuAccionesFila from './MenuAccionesFila';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import '../../style/components/GestionDatosMaestros.css';
import { coincideBusqueda } from '../../utils/textoComparable';

const GestionLesiones = () => {
    const [lesiones, setLesiones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedLesion, setSelectedLesion] = useState(null);
    const [formData, setFormData] = useState({
        nombre: ''
    });
    const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

    const [showModalEliminar, setShowModalEliminar] = useState(false);
    const [lesionAEliminar, setLesionAEliminar] = useState(null);
    const [eliminando, setEliminando] = useState(false);

    const [filterNombre, setFilterNombre] = useState('');

    useEffect(() => {
        cargarLesiones();
    }, []);

    const cargarLesiones = async () => {
        setLoading(true);
        try {
            const data = await getLesiones();
            setLesiones(data || []);
        } catch (error) {
            mostrarAlerta('Error al cargar lesiones', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const mostrarAlerta = (message, variant) => {
        setAlert({ show: true, message, variant });
        setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
    };

    const handleOpenModal = (mode, lesion = null) => {
        setModalMode(mode);

        if (mode === 'edit' && lesion) {
            setSelectedLesion(lesion);
            setFormData({ nombre: lesion.nombre || '' });
        } else {
            setSelectedLesion(null);
            setFormData({ nombre: '' });
        }

        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedLesion(null);
        setFormData({ nombre: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (modalMode === 'create') {
                await crearLesion(formData);
                mostrarAlerta('✅ Lesion creada exitosamente', 'success');
            } else {
                await actualizarLesion(selectedLesion.idLesion, formData);
                mostrarAlerta('✅ Lesion actualizada exitosamente', 'success');
            }

            handleCloseModal();
            await cargarLesiones();
        } catch (error) {
            mostrarAlerta('❌ Error al guardar la lesion', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id) => {
        const lesion = lesiones.find((l) => l.idLesion === id);
        setLesionAEliminar(lesion);
        setShowModalEliminar(true);
    };

    const confirmarEliminar = async () => {
        if (!lesionAEliminar) return;

        setEliminando(true);
        setLoading(true);

        try {
            await eliminarLesion(lesionAEliminar.idLesion);
            mostrarAlerta('✅ Lesion eliminada exitosamente', 'success');
            setShowModalEliminar(false);
            setLesionAEliminar(null);
            await cargarLesiones();
        } catch (error) {
            mostrarAlerta('❌ Error al eliminar la lesion', 'danger');
            setShowModalEliminar(false);
            setLesionAEliminar(null);
        } finally {
            setEliminando(false);
            setLoading(false);
        }
    };

    const cancelarEliminar = () => {
        setShowModalEliminar(false);
        setLesionAEliminar(null);
    };

    const getLesionesFiltradas = () => {
        let resultado = [...lesiones];

        if (filterNombre.trim()) {
            resultado = resultado.filter((l) => coincideBusqueda(l.nombre, filterNombre));
        }

        return resultado;
    };

    return (
        <div className="gestion-datos-maestros">
            {alert.show && (
                <Alert
                    variant={alert.variant}
                    dismissible
                    onClose={() => setAlert({ show: false, message: '', variant: '' })}
                >
                    {alert.message}
                </Alert>
            )}

            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex flex-wrap gap-2">
                    <Button
                        variant="outline-secondary"
                        onClick={cargarLesiones}
                        disabled={loading}
                        className="shadow-sm"
                        title="Recargar lista"
                    >
                        <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
                    </Button>

                    <Button
                        variant="primary"
                        onClick={() => handleOpenModal('create')}
                        className="shadow-sm"
                    >
                        <i className="bi bi-plus-circle me-2"></i>
                        Nueva Lesion
                    </Button>
                </div>
            </div>

            <PanelFiltros
                hayFiltros={filterNombre.trim() !== ''}
                onLimpiar={() => setFilterNombre('')}
                resumen={`Mostrando ${getLesionesFiltradas().length} de ${lesiones.length} lesiones`}
            >
                <CampoFiltro etiqueta="Buscar" icono="bi-search" htmlFor="filtro-lesion-nombre" ancho>
                    <Form.Control
                        id="filtro-lesion-nombre"
                        className="panel-filtros-input"
                        type="text"
                        placeholder="Nombre de la lesión (ej: Esguince, Luxación)…"
                        value={filterNombre}
                        onChange={(e) => setFilterNombre(e.target.value)}
                    />
                </CampoFiltro>
            </PanelFiltros>

            {loading && lesiones.length === 0 ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="datos-maestros-table">
                        <thead>
                            <tr>
                                <th>Lesion</th>
                                <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {getLesionesFiltradas().length === 0 ? (
                                <tr>
                                    <td colSpan="2" className="text-center text-muted py-4">
                                        {lesiones.length === 0
                                            ? 'No hay lesiones registradas'
                                            : 'No se encontraron lesiones con los filtros aplicados'}
                                    </td>
                                </tr>
                            ) : (
                                getLesionesFiltradas().map((lesion) => (
                                    <tr key={lesion.idLesion}>
                                        <td className="fw-semibold">{lesion.nombre}</td>
                                        <td className="text-end">
                                            <MenuAccionesFila acciones={[
                                                { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleOpenModal('edit', lesion) },
                                                { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleDelete(lesion.idLesion) },
                                            ]} />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Nueva Lesion' : 'Editar Lesion'}
                    </Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.nombre}
                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                required
                                placeholder="Ej: Esguince de tobillo"
                            />
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <ModalConfirmacionCambioEstado
                isOpen={showModalEliminar}
                onClose={cancelarEliminar}
                onConfirm={confirmarEliminar}
                titulo="Eliminar Lesion"
                mensaje={
                    lesionAEliminar
                        ? `¿Esta seguro de eliminar la lesion "${lesionAEliminar.nombre}"?\n\nEsta accion no se puede deshacer.`
                        : '¿Esta seguro de que desea eliminar esta lesion?'
                }
                cargando={eliminando}
                textoBotonConfirmar="Eliminar"
                textoBotonCancelar="Cancelar"
                variantBotonConfirmar="danger"
            />
        </div>
    );
};

export default GestionLesiones;

